(function () {
  const MAX_FILE_SIZE = 10 * 1024 * 1024;
  const MIN_CROP_FRAME_SIZE = 96;
  const MAX_IMAGE_ZOOM = 2.5;
  const MAX_UPLOAD_EDGE = 1024;
  const JPEG_QUALITY = 0.85;
  const IMAGE_SEARCH_ENDPOINT = "https://api.enroutejewelry.com/image_search";
  const SUPPORTED_TYPES = new Set(["image/jpeg", "image/png", "image/webp"]);

  async function recognizeImage(payload, options) {
    const response = await fetch(IMAGE_SEARCH_ENDPOINT, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-api-key": "f8f76332fd5cb6cefe5674207a6132a0",
      },
      body: JSON.stringify({
        image: payload.image,
        top_k: 10,
      }),
      signal: options?.signal,
    });

    if (!response.ok) {
      throw new Error(`Image search request failed (${response.status})`);
    }

    const data = await response.json();
    if (!Array.isArray(data?.results)) {
      throw new Error("Invalid image search response");
    }
    return data;
  }

  class ImageSearchFlow extends HTMLElement {
    constructor() {
      super();
      this.onClick = this.onClick.bind(this);
      this.onFileChange = this.onFileChange.bind(this);
      this.onDragOver = this.onDragOver.bind(this);
      this.onDragLeave = this.onDragLeave.bind(this);
      this.onDrop = this.onDrop.bind(this);
      this.capturePhoto = this.capturePhoto.bind(this);
      this.onModalClosed = this.onModalClosed.bind(this);
      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.onCropStageResize = this.onCropStageResize.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.zoom = 1;
      this.imageOffsetX = 0;
      this.imageOffsetY = 0;
      this.imagePointers = new Map();
    }

    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.searchPopup = this.closest("predictive-search-popup");
      this.searchWrapper = this.searchPopup?.querySelector(":scope > .wrapper");
      this.cameraInput = this.querySelector("[data-image-search-camera-input]");
      this.libraryInput = this.querySelector("[data-image-search-library-input]");
      this.modals = {
        source: document.getElementById("image-search-source-modal"),
        camera: document.getElementById("image-search-camera-modal"),
        crop: document.getElementById("image-search-crop-modal"),
      };
      this.resultsView = this.searchPopup?.querySelector("#image-search-results");
      this.resultsView?.addEventListener("click", this.onClick);
      this.dropZone = this.modals.source?.querySelector("[data-image-search-drop-zone]");
      this.cropStage = this.modals.crop?.querySelector("[data-image-search-crop-stage]");
      this.cropFrame = this.modals.crop?.querySelector("[data-image-search-crop-frame]");
      this.preview = this.modals.crop?.querySelector("[data-image-search-preview]");
      this.resultPreview = this.resultsView?.querySelector("[data-image-search-result-preview]");
      this.video = this.modals.camera?.querySelector("[data-image-search-video]");
      this.cameraLoading = this.modals.camera?.querySelector("[data-image-search-camera-loading]");
      this.results = this.resultsView?.querySelector("[data-image-search-results]");
      this.skeletonHTML = this.resultsView?.querySelector("[data-image-search-skeleton]")?.innerHTML.trim() || "";
      this.error = this.modals.source?.querySelector("[data-image-search-error]");

      document.querySelectorAll("[data-image-search-trigger]").forEach((trigger) => {
        trigger.addEventListener("click", this.onClick);
      });
      for (const modal of Object.values(this.modals)) modal?.addEventListener("click", this.onClick);
      this.cameraInput?.addEventListener("change", this.onFileChange);
      this.libraryInput?.addEventListener("change", this.onFileChange);
      this.dropZone?.addEventListener("dragover", this.onDragOver);
      this.dropZone?.addEventListener("dragleave", this.onDragLeave);
      this.dropZone?.addEventListener("drop", this.onDrop);
      this.cropStage?.addEventListener("pointerdown", this.onPointerDown);
      this.cropStage?.addEventListener("pointermove", this.onPointerMove);
      this.cropStage?.addEventListener("pointerup", this.onPointerUp);
      this.cropStage?.addEventListener("pointercancel", this.onPointerUp);
      if (this.cropFrame && "ResizeObserver" in window) {
        this.cropResizeObserver = new ResizeObserver(() => this.onCropFrameResize());
        this.cropResizeObserver.observe(this.cropFrame);
      }
      if (this.cropStage && "ResizeObserver" in window) {
        this.cropStageResizeObserver = new ResizeObserver(this.onCropStageResize);
        this.cropStageResizeObserver.observe(this.cropStage);
      }
      this.modals.camera?.querySelector("[data-image-search-capture]")?.addEventListener("click", this.capturePhoto);
      document.body.addEventListener("modalClosed", this.onModalClosed);
      document.addEventListener("keydown", this.onKeydown, true);
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.stopCamera();
      this.releaseObjectUrl();
      document.querySelectorAll("[data-image-search-trigger]").forEach((trigger) => {
        trigger.removeEventListener("click", this.onClick);
      });
      for (const modal of Object.values(this.modals || {})) modal?.removeEventListener("click", this.onClick);
      this.cameraInput?.removeEventListener("change", this.onFileChange);
      this.libraryInput?.removeEventListener("change", this.onFileChange);
      this.dropZone?.removeEventListener("dragover", this.onDragOver);
      this.dropZone?.removeEventListener("dragleave", this.onDragLeave);
      this.dropZone?.removeEventListener("drop", this.onDrop);
      this.cropStage?.removeEventListener("pointerdown", this.onPointerDown);
      this.cropStage?.removeEventListener("pointermove", this.onPointerMove);
      this.cropStage?.removeEventListener("pointerup", this.onPointerUp);
      this.cropStage?.removeEventListener("pointercancel", this.onPointerUp);
      this.cropResizeObserver?.disconnect();
      this.cropStageResizeObserver?.disconnect();
      window.cancelAnimationFrame(this.cropStageResizeFrame);
      this.modals?.camera?.querySelector("[data-image-search-capture]")?.removeEventListener("click", this.capturePhoto);
      this.resultsView?.removeEventListener("click", this.onClick);
      document.body.removeEventListener("modalClosed", this.onModalClosed);
      document.removeEventListener("keydown", this.onKeydown, true);
    }

    onClick(event) {
      const trigger = event.target.closest("[data-image-search-trigger]");
      if (trigger) {
        event.preventDefault();
        this.open(trigger);
        return;
      }

      const source = event.target.closest("[data-image-search-source]")?.dataset.imageSearchSource;
      // todo: review camera
      if (source === "camera") this.openCamera();
      // todo: review library
      if (source === "library") this.openLibrary();
      if (event.target.closest("[data-image-search-back]")) {
        if (event.target.closest(".image-search__reselect") && this.usesMobileImagePicker()) {
          this.openLibrary();
        } else {
          this.dismissOperation();
        }
      }
      if (event.target.closest("[data-image-search-submit]")) this.search();
      if (event.target.closest("[data-image-search-reset]")) this.initializeCropFrame();
      if (event.target.closest("[data-image-search-change]")) {
        if (this.usesMobileImagePicker()) this.openLibrary();
        else this.showPanel("source");
      }
      if (event.target.closest("[data-image-search-refocus]")) this.reselectFocus();
      if (event.target.closest("[data-image-search-text-search]")) this.backToTextSearch();
    }

    open(trigger) {
      this.returnFocusTo = trigger;
      this.textSearchInput = trigger.closest("search-field")?.querySelector("[data-search-input]");
      if (this.usesMobileImagePicker()) {
        this.openLibrary();
        return;
      }
      this.activate();
      this.showPanel("source");
    }

    activate() {
      if (this.active) return;
      this.active = true;
      this.searchPopup?.open();
      this.searchPopup?.core.clearPending();
      this.updateSearchInteractivity();
    }

    updateSearchInteractivity() {
      if (!this.searchWrapper) return;
      const editing = this.active && this.activePanel !== "results";
      this.searchWrapper.inert = editing;
      if (editing) this.searchWrapper.setAttribute("aria-hidden", "true");
      else this.searchWrapper.removeAttribute("aria-hidden");
      this.searchPopup.toggleAttribute("data-image-search-active", this.active);
      // Editing is modal; browsing results leaves both navigation and input available.
      const header = this.searchPopup.closest("site-header")?.querySelector("header");
      if (header) header.inert = editing;
    }

    dismissOperation() {
      if (this.lastResults) {
        const previous = this.lastResults;
        this.abortController?.abort();
        this.setSearching(false);
        if (this.file !== previous.file) this.useFile(previous.file);
        this.results.replaceChildren(...previous.nodes);
        this.resultPreview.src = previous.preview;
        this.showPanel("results");
      } else this.close();
    }

    usesMobileImagePicker() {
      return window.matchMedia(
        "(max-width: 767px), (max-width: 1024px) and (orientation: portrait)"
      ).matches;
    }

    getBaseImageScale(naturalWidth, naturalHeight, baseWidth, baseHeight) {
      return Math.min(baseWidth / naturalWidth, baseHeight / naturalHeight);
    }

    close(focusTarget = this.returnFocusTo) {
      this.abortController?.abort();
      this.setSearching(false);
      this.stopCamera();
      this.imagePointers.clear();
      this.resizing = null;
      this.desktopSelection = null;
      delete this.cropFrame?.dataset.resizing;
      delete this.cropStage?.dataset.selecting;
      delete this.cropStage?.dataset.interacting;
      if (this.activeModal?.hasAttribute("open")) {
        this.switchingModals = true;
        this.activeModal.hide();
        this.switchingModals = false;
      }
      this.active = false;
      this.lastResults = null;
      delete this.resultsView?.dataset.searchQuery;
      this.restoreSearchPageUrl();
      this.searchPopup?.setInputVisible(true);
      if (this.resultsView) this.resultsView.hidden = true;
      this.activePanel = null;
      this.activeModal = null;
      this.updateSearchInteractivity();
      focusTarget?.focus();
    }

    backToTextSearch() {
      this.close(this.textSearchInput);
    }

    reselectFocus() {
      if (!this.file || !this.objectUrl) {
        this.showPanel("source");
        return;
      }

      // Recognition previews are cropped data URLs. Restore the retained upload so the
      // customer always adjusts the focus frame against the full-resolution original.
      this.preview.src = this.objectUrl;
      this.showPanel("crop");
      window.requestAnimationFrame(() => {
        this.constrainFrameOffset();
        this.updateTransform();
      });
    }

    showPanel(name) {
      if (name !== "results" && this.searching) {
        this.abortController?.abort();
        this.setSearching(false);
      }
      if (this.activePanel === "camera" && name !== "camera") this.stopCamera();
      const nextModal = this.modals[name];
      if (name !== "results" && !nextModal) return;
      if (this.activePanel === name) return;

      if (this.activeModal?.hasAttribute("open")) {
        this.switchingModals = true;
        this.activeModal.hide();
        this.switchingModals = false;
      }
      this.activePanel = name;
      this.activeModal = nextModal || null;
      if (name === "results") {
        this.searchPopup?.setInputVisible(false);
        this.resultsView.hidden = false;
      }
      this.updateSearchInteractivity();
      if (nextModal) nextModal.show(this.returnFocusTo);
      window.requestAnimationFrame(() => {
        if (this.activePanel !== name) return;
        if (name === "results") this.resultsView.focus({ preventScroll: true });
        else {
          const [firstFocusable] = this.getFocusableElements(nextModal);
          (firstFocusable || nextModal.querySelector("[role='dialog'], [role='status']"))?.focus();
        }
      });
    }

    getFocusableElements(modal = this.activeModal) {
      return [...(modal?.querySelectorAll("button:not([disabled]), input:not([disabled])") || [])]
        .filter((element) => element.offsetParent !== null);
    }

    onModalClosed() {
      if (this.switchingModals || !this.activeModal) return;
      // ModalDialog emits before removing its open attribute.
      const modal = this.activeModal;
      queueMicrotask(() => {
        if (this.activeModal === modal && !modal.hasAttribute("open")) this.dismissOperation();
      });
    }

    openLibrary() {
      if (!this.libraryInput) return;
      if (this.searching && !this.usesMobileImagePicker()) this.cancelSearch();
      // `capture` is what asks mobile browsers for a camera source. Removing it
      // immediately before opening keeps this control dedicated to the photo library.
      this.libraryInput.removeAttribute("capture");
      this.libraryInput.accept = "image/*";
      if (typeof this.libraryInput.showPicker === "function") {
        try {
          this.libraryInput.showPicker();
          return;
        } catch (_error) {
          // Older mobile browsers expose showPicker but only allow a click fallback.
        }
      }
      this.libraryInput.click();
    }

    async openCamera() {
      this.error.hidden = true;
      const useNativeCamera = this.usesMobileImagePicker();
      if (useNativeCamera || !navigator.mediaDevices?.getUserMedia) {
        this.cameraInput?.click();
        return;
      }

      this.showPanel("camera");
      this.cameraLoading.hidden = false;
      try {
        this.cameraStream = await navigator.mediaDevices.getUserMedia({
          audio: false,
          video: { facingMode: { ideal: "environment" } },
        });
        if (this.activePanel !== "camera") {
          this.stopCamera();
          return;
        }
        this.video.srcObject = this.cameraStream;
        await this.video.play();
        this.cameraLoading.hidden = true;
      } catch (_error) {
        this.stopCamera();
        this.showPanel("source");
        this.error.textContent = this.dataset.cameraError;
        this.error.hidden = false;
      }
    }

    capturePhoto() {
      if (!this.video?.videoWidth || !this.cameraStream) return;
      const canvas = document.createElement("canvas");
      canvas.width = this.video.videoWidth;
      canvas.height = this.video.videoHeight;
      canvas.getContext("2d")?.drawImage(this.video, 0, 0, canvas.width, canvas.height);
      canvas.toBlob((blob) => {
        if (!blob || this.activePanel !== "camera") return;
        const photo = new File([blob], `camera-${Date.now()}.jpg`, { type: "image/jpeg" });
        this.useFile(photo);
      }, "image/jpeg", 0.92);
    }

    stopCamera() {
      for (const track of this.cameraStream?.getTracks() || []) track.stop();
      this.cameraStream = null;
      if (this.video) this.video.srcObject = null;
    }

    onFileChange(event) {
      const [file] = event.target.files || [];
      event.target.value = "";
      if (file) this.useFile(file);
    }

    onDragOver(event) {
      event.preventDefault();
      this.dropZone?.classList.add("is-dragging");
    }

    onDragLeave() {
      this.dropZone?.classList.remove("is-dragging");
    }

    onDrop(event) {
      event.preventDefault();
      this.onDragLeave();
      const [file] = event.dataTransfer?.files || [];
      if (file) this.useFile(file);
    }

    useFile(file) {
      if (!SUPPORTED_TYPES.has(file.type) || file.size > MAX_FILE_SIZE) {
        this.error.textContent = file.size > MAX_FILE_SIZE
          ? this.dataset.fileTooLarge
          : this.dataset.invalidType;
        this.error.hidden = false;
        return;
      }

      this.activate();
      this.error.hidden = true;
      this.file = file;
      this.releaseObjectUrl();
      this.objectUrl = URL.createObjectURL(file);
      this.preview.src = this.objectUrl;
      this.cropBaseWidth = 0;
      this.cropBaseHeight = 0;
      this.cropFrameWidth = 0;
      this.cropFrameHeight = 0;
      this.frameOffsetX = 0;
      this.frameOffsetY = 0;
      this.zoom = 1;
      this.imageOffsetX = 0;
      this.imageOffsetY = 0;
      this.imagePointers.clear();
      this.cropFrame.style.removeProperty("inline-size");
      this.cropFrame.style.removeProperty("block-size");
      this.cropFrame.style.removeProperty("aspect-ratio");
      this.cropFrame.style.removeProperty("transform");
      this.updateTransform();
      this.showPanel("crop");
      window.requestAnimationFrame(() => this.initializeCropFrame());
    }

    onPointerDown(event) {
      if (!this.file || event.button !== 0 || this.resizing) return;

      const resizeHandle = event.target.closest("[data-image-search-resize]");
      if (resizeHandle && this.imagePointers.size === 0 && !this.desktopSelection) {
        event.preventDefault();
        this.cropStage.setPointerCapture(event.pointerId);
        const frameBounds = this.cropFrame.getBoundingClientRect();
        this.resizing = {
          pointerId: event.pointerId,
          direction: resizeHandle.dataset.imageSearchResize,
          startX: event.clientX,
          startY: event.clientY,
          startWidth: frameBounds.width,
          startHeight: frameBounds.height,
          startFrameX: this.frameOffsetX,
          startFrameY: this.frameOffsetY,
        };
        this.cropFrame.dataset.resizing = this.resizing.direction;
        return;
      }

      if (event.pointerType !== "mouse") {
        event.preventDefault();
        if (this.imagePointers.size >= 2) return;
        this.cropStage.setPointerCapture(event.pointerId);
        this.cropStage.dataset.interacting = "true";
        this.imagePointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
        if (this.imagePointers.size === 1) {
          this.startImagePan(event.clientX, event.clientY);
        } else if (this.imagePointers.size === 2) {
          this.startImagePinch();
        }
        return;
      }

      if (this.imagePointers.size || this.desktopSelection) return;
      const point = this.getDesktopImagePoint(event);
      if (!point) return;
      event.preventDefault();
      this.cropStage.setPointerCapture(event.pointerId);
      const frameBounds = this.cropFrame.getBoundingClientRect();
      this.desktopSelection = {
        pointerId: event.pointerId,
        startX: point.x,
        startY: point.y,
        previousWidth: frameBounds.width,
        previousHeight: frameBounds.height,
        previousFrameX: this.frameOffsetX,
        previousFrameY: this.frameOffsetY,
      };
      this.cropStage.dataset.selecting = "true";
    }

    onPointerMove(event) {
      if (this.resizing?.pointerId === event.pointerId) {
        event.preventDefault();
        const horizontalDirection = this.resizing.direction.endsWith("l") ? -1 : 1;
        const verticalDirection = this.resizing.direction.startsWith("t") ? -1 : 1;
        const deltaX = (event.clientX - this.resizing.startX) * horizontalDirection;
        const deltaY = (event.clientY - this.resizing.startY) * verticalDirection;
        const renderedImage = this.getRenderedImageSize();
        const imageLeft = this.imageOffsetX - renderedImage.width / 2;
        const imageRight = this.imageOffsetX + renderedImage.width / 2;
        const imageTop = this.imageOffsetY - renderedImage.height / 2;
        const imageBottom = this.imageOffsetY + renderedImage.height / 2;
        const horizontalAnchor = this.resizing.startFrameX
          - horizontalDirection * this.resizing.startWidth / 2;
        const verticalAnchor = this.resizing.startFrameY
          - verticalDirection * this.resizing.startHeight / 2;
        const maximumWidth = horizontalDirection > 0
          ? imageRight - horizontalAnchor
          : horizontalAnchor - imageLeft;
        const maximumHeight = verticalDirection > 0
          ? imageBottom - verticalAnchor
          : verticalAnchor - imageTop;
        const minimumWidth = Math.min(MIN_CROP_FRAME_SIZE, maximumWidth);
        const minimumHeight = Math.min(MIN_CROP_FRAME_SIZE, maximumHeight);
        const width = Math.max(
          minimumWidth,
          Math.min(maximumWidth, this.resizing.startWidth + deltaX)
        );
        const height = Math.max(
          minimumHeight,
          Math.min(maximumHeight, this.resizing.startHeight + deltaY)
        );
        this.cropFrameWidth = width;
        this.cropFrameHeight = height;
        this.frameOffsetX = horizontalAnchor + horizontalDirection * width / 2;
        this.frameOffsetY = verticalAnchor + verticalDirection * height / 2;
        this.cropFrame.style.inlineSize = `${width}px`;
        this.cropFrame.style.blockSize = `${height}px`;
        this.cropFrame.style.aspectRatio = "auto";
        this.cropFrame.style.transform = `translate3d(${this.frameOffsetX}px, ${this.frameOffsetY}px, 0)`;
        this.constrainFrameOffset();
        this.constrainImageOffset();
        this.updateTransform();
        return;
      }

      if (event.pointerType !== "mouse" && this.imagePointers.has(event.pointerId)) {
        event.preventDefault();
        this.imagePointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
        if (this.imagePointers.size >= 2) {
          const center = this.getImageGestureCenter();
          const distance = this.getImagePointerDistance();
          const nextZoom = this.pinchStartZoom * distance / this.pinchStartDistance;
          this.zoom = Math.max(1, Math.min(MAX_IMAGE_ZOOM, nextZoom));
          this.imageOffsetX = center.x - this.pinchImageX * this.zoom;
          this.imageOffsetY = center.y - this.pinchImageY * this.zoom;
        } else {
          this.imageOffsetX = this.panStartOffsetX + event.clientX - this.panStartX;
          this.imageOffsetY = this.panStartOffsetY + event.clientY - this.panStartY;
        }
        this.constrainImageOffset();
        this.updateTransform();
        return;
      }

      if (this.desktopSelection?.pointerId !== event.pointerId) return;
      event.preventDefault();
      this.updateDesktopSelection(this.getDesktopImagePoint(event, true));
    }

    onPointerUp(event) {
      if (this.resizing?.pointerId === event.pointerId) {
        this.resizing = null;
        delete this.cropFrame.dataset.resizing;
        return;
      }

      if (event.pointerType !== "mouse" && this.imagePointers.has(event.pointerId)) {
        this.imagePointers.delete(event.pointerId);
        if (this.imagePointers.size === 1) {
          const pointer = this.imagePointers.values().next().value;
          this.startImagePan(pointer.x, pointer.y);
        } else if (this.imagePointers.size === 0) {
          this.pinchStartDistance = null;
          delete this.cropStage.dataset.interacting;
        }
        return;
      }

      if (this.desktopSelection?.pointerId !== event.pointerId) return;
      event.preventDefault();
      const selection = this.desktopSelection;
      const point = this.getDesktopImagePoint(event, true);
      const width = Math.abs(point.x - selection.startX);
      const height = Math.abs(point.y - selection.startY);
      if (event.type === "pointercancel" || width < 8 || height < 8) {
        this.cropFrameWidth = selection.previousWidth;
        this.cropFrameHeight = selection.previousHeight;
        this.frameOffsetX = selection.previousFrameX;
        this.frameOffsetY = selection.previousFrameY;
        this.cropFrame.style.inlineSize = `${selection.previousWidth}px`;
        this.cropFrame.style.blockSize = `${selection.previousHeight}px`;
      } else {
        this.updateDesktopSelection(point);
      }
      this.desktopSelection = null;
      delete this.cropStage.dataset.selecting;
      this.constrainFrameOffset();
      this.updateTransform();
    }

    getDesktopImagePoint(event, clampToImage = false) {
      const bounds = this.cropStage.getBoundingClientRect();
      const renderedImage = this.getRenderedImageSize();
      const x = event.clientX - bounds.left - bounds.width / 2;
      const y = event.clientY - bounds.top - bounds.height / 2;
      const left = this.imageOffsetX - renderedImage.width / 2;
      const right = this.imageOffsetX + renderedImage.width / 2;
      const top = this.imageOffsetY - renderedImage.height / 2;
      const bottom = this.imageOffsetY + renderedImage.height / 2;
      if (!clampToImage && (x < left || x > right || y < top || y > bottom)) return null;
      return {
        x: Math.max(left, Math.min(right, x)),
        y: Math.max(top, Math.min(bottom, y)),
      };
    }

    updateDesktopSelection(point) {
      if (!point || !this.desktopSelection) return;
      const left = Math.min(this.desktopSelection.startX, point.x);
      const top = Math.min(this.desktopSelection.startY, point.y);
      const width = Math.abs(point.x - this.desktopSelection.startX);
      const height = Math.abs(point.y - this.desktopSelection.startY);
      this.cropFrameWidth = width;
      this.cropFrameHeight = height;
      this.frameOffsetX = left + width / 2;
      this.frameOffsetY = top + height / 2;
      this.cropFrame.style.inlineSize = `${width}px`;
      this.cropFrame.style.blockSize = `${height}px`;
      this.cropFrame.style.aspectRatio = "auto";
      this.updateFrameTransform();
    }

    startImagePan(x, y) {
      this.panStartX = x;
      this.panStartY = y;
      this.panStartOffsetX = this.imageOffsetX;
      this.panStartOffsetY = this.imageOffsetY;
    }

    startImagePinch() {
      const center = this.getImageGestureCenter();
      this.pinchStartDistance = this.getImagePointerDistance() || 1;
      this.pinchStartZoom = this.zoom;
      this.pinchImageX = (center.x - this.imageOffsetX) / this.zoom;
      this.pinchImageY = (center.y - this.imageOffsetY) / this.zoom;
    }

    getImageGestureCenter() {
      const pointers = [...this.imagePointers.values()].slice(0, 2);
      const bounds = this.cropStage.getBoundingClientRect();
      return {
        x: (pointers[0].x + pointers[1].x) / 2 - bounds.left - bounds.width / 2,
        y: (pointers[0].y + pointers[1].y) / 2 - bounds.top - bounds.height / 2,
      };
    }

    getImagePointerDistance() {
      const [first, second] = [...this.imagePointers.values()];
      return Math.hypot(second.x - first.x, second.y - first.y);
    }

    getRenderedImageSize() {
      const viewport = this.getCropViewportSize();
      const baseWidth = this.cropBaseWidth || viewport.width;
      const baseHeight = this.cropBaseHeight || viewport.height;
      const naturalWidth = this.preview?.naturalWidth || 0;
      const naturalHeight = this.preview?.naturalHeight || 0;
      if (!baseWidth || !baseHeight || !naturalWidth || !naturalHeight) {
        return { width: baseWidth, height: baseHeight };
      }
      const scale = this.getBaseImageScale(naturalWidth, naturalHeight, baseWidth, baseHeight);
      return {
        width: naturalWidth * scale * this.zoom,
        height: naturalHeight * scale * this.zoom,
      };
    }

    getCropViewportSize() {
      if (!this.cropStage) return { width: 0, height: 0 };
      const styles = window.getComputedStyle(this.cropStage);
      const horizontalPadding = parseFloat(styles.paddingLeft) + parseFloat(styles.paddingRight);
      const verticalPadding = parseFloat(styles.paddingTop) + parseFloat(styles.paddingBottom);
      return {
        width: Math.max(0, this.cropStage.clientWidth - horizontalPadding),
        height: Math.max(0, this.cropStage.clientHeight - verticalPadding),
      };
    }

    constrainFrameOffset() {
      const frameBounds = this.cropFrame?.getBoundingClientRect();
      const frameWidth = frameBounds?.width || this.cropFrameWidth || 0;
      const frameHeight = frameBounds?.height || this.cropFrameHeight || 0;
      const renderedImage = this.getRenderedImageSize();
      const minimumX = this.imageOffsetX - renderedImage.width / 2 + frameWidth / 2;
      const maximumX = this.imageOffsetX + renderedImage.width / 2 - frameWidth / 2;
      const minimumY = this.imageOffsetY - renderedImage.height / 2 + frameHeight / 2;
      const maximumY = this.imageOffsetY + renderedImage.height / 2 - frameHeight / 2;
      this.frameOffsetX = Math.max(minimumX, Math.min(maximumX, this.frameOffsetX));
      this.frameOffsetY = Math.max(minimumY, Math.min(maximumY, this.frameOffsetY));
    }

    constrainImageOffset() {
      const frameBounds = this.cropFrame?.getBoundingClientRect();
      const frameWidth = frameBounds?.width || this.cropFrameWidth || 0;
      const frameHeight = frameBounds?.height || this.cropFrameHeight || 0;
      const renderedImage = this.getRenderedImageSize();
      const cropLeft = this.frameOffsetX - frameWidth / 2;
      const cropRight = this.frameOffsetX + frameWidth / 2;
      const cropTop = this.frameOffsetY - frameHeight / 2;
      const cropBottom = this.frameOffsetY + frameHeight / 2;
      this.imageOffsetX = Math.max(
        cropRight - renderedImage.width / 2,
        Math.min(cropLeft + renderedImage.width / 2, this.imageOffsetX)
      );
      this.imageOffsetY = Math.max(
        cropBottom - renderedImage.height / 2,
        Math.min(cropTop + renderedImage.height / 2, this.imageOffsetY)
      );
    }

    updateFrameTransform() {
      this.cropFrame.style.transform = `translate3d(${this.frameOffsetX}px, ${this.frameOffsetY}px, 0)`;
    }

    initializeCropFrame() {
      if (!this.file || this.activePanel !== "crop") return;
      const frameBounds = this.cropFrame?.getBoundingClientRect();
      const width = frameBounds?.width || 0;
      const height = frameBounds?.height || 0;
      const viewport = this.getCropViewportSize();
      const stageWidth = viewport.width;
      const stageHeight = viewport.height;
      if (!width || !height || !stageWidth || !stageHeight) return;
      this.cropBaseWidth = stageWidth;
      this.cropBaseHeight = stageHeight;
      const naturalWidth = this.preview?.naturalWidth || 0;
      const naturalHeight = this.preview?.naturalHeight || 0;
      if (!naturalWidth || !naturalHeight) {
        this.preview?.addEventListener("load", () => this.initializeCropFrame(), { once: true });
        return;
      }
      const imageScale = this.getBaseImageScale(naturalWidth, naturalHeight, stageWidth, stageHeight);
      const frameWidth = naturalWidth * imageScale;
      const frameHeight = naturalHeight * imageScale;
      this.cropFrameWidth = frameWidth;
      this.cropFrameHeight = frameHeight;
      this.frameOffsetX = 0;
      this.frameOffsetY = 0;
      this.zoom = 1;
      this.imageOffsetX = 0;
      this.imageOffsetY = 0;
      this.cropFrame.style.inlineSize = `${frameWidth}px`;
      this.cropFrame.style.blockSize = `${frameHeight}px`;
      this.cropFrame.style.aspectRatio = "auto";
      this.constrainFrameOffset();
      this.updateTransform();
    }

    onCropStageResize() {
      window.cancelAnimationFrame(this.cropStageResizeFrame);
      this.cropStageResizeFrame = window.requestAnimationFrame(() => {
        if (!this.file || this.activePanel !== "crop") return;
        const viewport = this.getCropViewportSize();
        if (!viewport.width || !viewport.height) return;
        if (!this.cropBaseWidth || !this.cropBaseHeight) {
          this.initializeCropFrame();
          return;
        }
        if (
          Math.abs(viewport.width - this.cropBaseWidth) < 0.5
          && Math.abs(viewport.height - this.cropBaseHeight) < 0.5
        ) return;

        const naturalWidth = this.preview?.naturalWidth || 0;
        const naturalHeight = this.preview?.naturalHeight || 0;
        if (!naturalWidth || !naturalHeight) return;
        const oldScale = this.getBaseImageScale(
          naturalWidth,
          naturalHeight,
          this.cropBaseWidth,
          this.cropBaseHeight
        );
        const newScale = this.getBaseImageScale(
          naturalWidth,
          naturalHeight,
          viewport.width,
          viewport.height
        );
        const scaleChange = newScale / oldScale;
        const frameBounds = this.cropFrame.getBoundingClientRect();

        this.cropBaseWidth = viewport.width;
        this.cropBaseHeight = viewport.height;
        this.cropFrameWidth = frameBounds.width * scaleChange;
        this.cropFrameHeight = frameBounds.height * scaleChange;
        this.frameOffsetX *= scaleChange;
        this.frameOffsetY *= scaleChange;
        this.imageOffsetX *= scaleChange;
        this.imageOffsetY *= scaleChange;
        this.cropFrame.style.inlineSize = `${this.cropFrameWidth}px`;
        this.cropFrame.style.blockSize = `${this.cropFrameHeight}px`;
        this.constrainFrameOffset();
        this.updateTransform();
      });
    }

    onCropFrameResize() {
      if (!this.cropBaseWidth || !this.cropBaseHeight || this.activePanel !== "crop") return;
      const frameBounds = this.cropFrame?.getBoundingClientRect();
      const width = frameBounds?.width || 0;
      const height = frameBounds?.height || 0;
      if (!width || !height) return;
      if (
        Math.abs(width - this.cropFrameWidth) < 0.5
        && Math.abs(height - this.cropFrameHeight) < 0.5
      ) return;
      this.cropFrameWidth = width;
      this.cropFrameHeight = height;
      this.constrainFrameOffset();
      this.constrainImageOffset();
      this.updateTransform();
    }

    updateTransform() {
      if (!this.preview) return;
      this.preview.style.transform = `translate3d(calc(-50% + ${this.imageOffsetX}px), calc(-50% + ${this.imageOffsetY}px), 0) scale(${this.zoom})`;
      this.updateFrameTransform();
    }

    async createSearchPayload() {
      try {
        await this.preview.decode();
      } catch (_error) {
        throw new Error("Unable to decode image");
      }

      const frameWidth = this.cropFrame.clientWidth;
      const frameHeight = this.cropFrame.clientHeight;
      const naturalWidth = this.preview.naturalWidth;
      const naturalHeight = this.preview.naturalHeight;
      if (!frameWidth || !frameHeight || !naturalWidth || !naturalHeight) {
        throw new Error("Invalid crop dimensions");
      }

      const baseWidth = this.cropBaseWidth || frameWidth;
      const baseHeight = this.cropBaseHeight || frameHeight;
      const scale = this.getBaseImageScale(
        naturalWidth,
        naturalHeight,
        baseWidth,
        baseHeight
      ) * this.zoom;
      const renderedWidth = naturalWidth * scale;
      const renderedHeight = naturalHeight * scale;
      const sourceX = Math.max(0, (renderedWidth / 2 + this.frameOffsetX - this.imageOffsetX - frameWidth / 2) / scale);
      const sourceY = Math.max(0, (renderedHeight / 2 + this.frameOffsetY - this.imageOffsetY - frameHeight / 2) / scale);
      const sourceWidth = Math.min(naturalWidth - sourceX, frameWidth / scale);
      const sourceHeight = Math.min(naturalHeight - sourceY, frameHeight / scale);
      const outputScale = Math.min(1, MAX_UPLOAD_EDGE / Math.max(sourceWidth, sourceHeight));
      const canvas = document.createElement("canvas");
      canvas.width = Math.max(1, Math.round(sourceWidth * outputScale));
      canvas.height = Math.max(1, Math.round(sourceHeight * outputScale));
      canvas.getContext("2d")?.drawImage(
        this.preview,
        sourceX,
        sourceY,
        sourceWidth,
        sourceHeight,
        0,
        0,
        canvas.width,
        canvas.height
      );
      const dataUrl = canvas.toDataURL("image/jpeg", JPEG_QUALITY);
      return {
        image: dataUrl.slice(dataUrl.indexOf(",") + 1),
        preview: dataUrl,
      };
    }

    async search() {
      if (!this.file || this.searching) return;
      this.abortController?.abort();
      this.abortController = new AbortController();
      const { signal } = this.abortController;
      this.setSearching(true);

      try {
        const payload = await this.createSearchPayload();
        if (signal.aborted) return;
        this.resultPreview.src = payload.preview;
        this.results.innerHTML = this.skeletonHTML;
        this.showPanel("results");

        const recognized = await window.EnrouteImageSearch.recognize(payload, { signal });
        if (signal.aborted) return;
        const searchQuery = `image_${recognized.query_id ?? ""}`;
        const handles = [...new Set(recognized.results
          .map((result) => typeof result?.handle === "string" ? result.handle.trim() : "")
          .filter(Boolean))];
        const rendered = await window.EnrouteSearch.renderProductHandles(
          handles,
          { nothingWithBonus: this.dataset.emptyResults || "" },
          signal
        );
        if (signal.aborted) return;
        this.results.innerHTML = rendered.html;
        this.resultsView.dataset.searchQuery = searchQuery;
        this.updateSearchPageUrl(searchQuery);
        this.lastResults = { nodes: [...this.results.childNodes], preview: payload.preview, file: this.file };
        this.setSearching(false);
        this.searchPopup.core.trackSearchQuery(searchQuery, rendered.count);
        if (typeof window.initVariantPickersInNewItems === "function") window.initVariantPickersInNewItems(this.results);
        if (typeof window.runAsyncActions === "function") window.runAsyncActions();
      } catch (error) {
        if (signal.aborted || error.name === "AbortError") return;
        console.log("image search error", error);
        this.showPanel("crop");
      }
    }

    updateSearchPageUrl(searchQuery) {
      if (!document.querySelector("#search-page--result")) return;
      const url = new URL(window.location.href);
      if (!this.searchPageUrl) this.searchPageUrl = { query: url.searchParams.get("q") };
      url.searchParams.set("q", searchQuery);
      window.history.replaceState(window.history.state, "", url.toString());
      this.searchPageUrl.imageQuery = searchQuery;
    }

    restoreSearchPageUrl() {
      if (!this.searchPageUrl) return;
      const url = new URL(window.location.href);
      if (url.searchParams.get("q") === this.searchPageUrl.imageQuery) {
        if (this.searchPageUrl.query === null) url.searchParams.delete("q");
        else url.searchParams.set("q", this.searchPageUrl.query);
        window.history.replaceState(window.history.state, "", url.toString());
      }
      this.searchPageUrl = null;
    }

    setSearching(searching) {
      this.searching = searching;
      this.resultsView?.classList.toggle("is-searching", searching);
      this.results?.setAttribute("aria-busy", String(searching));
    }

    cancelSearch() {
      if (!this.searching) return;
      this.abortController?.abort();
      this.abortController = null;
      this.showPanel("crop");
    }

    onKeydown(event) {
      if (!this.activeModal) return;
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.dismissOperation();
        return;
      }
      if (event.key !== "Tab") return;
      const focusable = this.getFocusableElements();
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable.at(-1);
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    releaseObjectUrl() {
      if (!this.objectUrl) return;
      URL.revokeObjectURL(this.objectUrl);
      this.objectUrl = null;
    }
  }

  window.EnrouteImageSearch = window.EnrouteImageSearch || {};
  window.EnrouteImageSearch.recognize = window.EnrouteImageSearch.recognize || recognizeImage;

  if (!window.customElements.get("image-search-flow")) {
    window.customElements.define("image-search-flow", ImageSearchFlow);
  }
})();
